import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { useEffect, useState } from "react";
import { Play } from "lucide-react";
import { cn } from "@/lib/utils";

// Thumb images from public folder
const thumbOriginal = "/assets/imgs/vsl-thumb-original.jpg";
const thumbVariant = "/assets/imgs/vsl-thumb-original.jpg"; // Using same image for now

interface ABTestSceneProps {
  isActive: boolean;
}

// Single metric for summary
// Two metrics for summary
const METRICS = [
  { name: "Tx. Conv.", a: 23.1, b: 26.7 },
  { name: "Play Rate", a: 69.7, b: 69.8 },
];

// Video thumbnail component
interface VideoThumbProps {
  isWinner?: boolean;
  conversionRate: string;
  label: string;
  imageSrc: string;
  visible: boolean;
  delay?: number;
}

function VideoThumb({ 
  isWinner, 
  conversionRate, 
  label, 
  imageSrc, 
  visible, 
  delay = 0
}: VideoThumbProps) {
  useLanguage();
  return (
    <div
      className={cn(
        "flex-1 rounded-xl border-2 p-2 transition-all duration-500",
        isWinner
          ? "border-[#1F6BFF] bg-[#1F6BFF]/[0.03] animate-winner-glow"
          : "border-gray-200 bg-gray-50",
        visible
          ? "opacity-100 translate-y-0"
          : "opacity-0 translate-y-2"
      )}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {/* Thumbnail with overlay */}
      <div className="relative aspect-video rounded-lg overflow-hidden">
        <img
          src={imageSrc}
          alt={translateInterfaceText("VSL {value0}", { value0: (label) })}
          className="w-full h-full object-cover"
        />
        {/* Play button overlay */}
        <div className="absolute inset-0 flex items-center justify-center bg-black/10">
          <div className={cn(
            "w-7 h-7 rounded-full flex items-center justify-center shadow-sm",
            isWinner ? "bg-white" : "bg-white/90"
          )}>
            <Play className={cn(
              "w-3.5 h-3.5 ml-0.5",
              isWinner ? "text-[#1F6BFF]" : "text-gray-600"
            )} />
          </div>
        </div>
      </div>

      {/* Main KPI */}
      <p className={cn(
        "text-[16px] font-bold text-center mt-2",
        isWinner ? "text-[#1F6BFF]" : "text-gray-700"
      )}>
        {conversionRate}
      </p>
      <p className="text-[8px] text-gray-400 text-center uppercase tracking-wider">
        {translateInterfaceText("Conversão")}</p>

      {/* Label */}
      <p className={cn(
        "text-[9px] font-medium text-center uppercase tracking-wide mt-1.5",
        isWinner ? "text-[#1F6BFF]" : "text-gray-500"
      )}>
        {label}
      </p>
    </div>
  );
}

// Metrics Summary component
interface MetricsSummaryProps {
  visible: boolean;
}

function MetricsSummary({ visible }: MetricsSummaryProps) {
  useLanguage();
  const [barsAnimated, setBarsAnimated] = useState(false);

  useEffect(() => {
    if (!visible) {
      setBarsAnimated(false);
      return;
    }
    const timer = setTimeout(() => setBarsAnimated(true), 400);
    return () => clearTimeout(timer);
  }, [visible]);

  return (
    <div
      className={cn(
        "bg-gray-50/50 rounded-xl p-3 transition-all duration-500",
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
      )}
      style={{ transitionDelay: "300ms" }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-2.5">
        <span className="text-[10px] font-semibold text-gray-700">
          {translateInterfaceText("Resumo (A vs B)")}</span>
        <span className="text-[8px] text-gray-400">
          {translateInterfaceText("Base: últimos 7 dias")}</span>
      </div>

      {/* Metrics Rows */}
      <div className="space-y-2">
        {METRICS.map((metric) => {
          const maxValue = Math.max(metric.a, metric.b);
          const barAWidth = (metric.a / maxValue) * 85;
          const barBWidth = (metric.b / maxValue) * 85;
          const delta = (metric.b - metric.a).toFixed(1);

          return (
            <div key={metric.name} className="grid grid-cols-[60px_1fr_auto] items-center gap-3">
              {/* Metric name */}
              <span className="text-[9px] text-gray-600 font-medium">
                {metric.name}
              </span>

              {/* Bars */}
              <div className="flex flex-col gap-1">
                <div className="h-[4px] bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gray-300 rounded-full transition-all duration-700 ease-out"
                    style={{ width: barsAnimated ? `${barAWidth}%` : "0%" }}
                  />
                </div>
                <div className="h-[4px] bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[#1F6BFF] rounded-full transition-all duration-700 ease-out"
                    style={{ width: barsAnimated ? `${barBWidth}%` : "0%" }}
                  />
                </div>
              </div>

              {/* Values + Delta */}
              <div className="flex items-center gap-2 text-[9px] tabular-nums">
                <div className="flex items-center gap-1">
                  <span className="text-gray-500 w-10 text-right">{metric.a}%</span>
                  <span className="text-gray-300">vs</span>
                  <span className="text-[#1F6BFF] font-medium w-10">{metric.b}%</span>
                </div>
                <span className="text-[8px] text-[#1F6BFF]/70 w-12 text-right">
                  +{delta} p.p.
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function ABTestScene({ isActive }: ABTestSceneProps) {
  useLanguage();
  const [cardsVisible, setCardsVisible] = useState(false);
  const [summaryVisible, setSummaryVisible] = useState(false);

  useEffect(() => {
    if (!isActive) {
      setCardsVisible(false);
      setSummaryVisible(false);
      return;
    }

    // Show cards first
    const cardsTimer = setTimeout(() => setCardsVisible(true), 100);
    // Show summary after cards
    const summaryTimer = setTimeout(() => setSummaryVisible(true), 300);

    return () => {
      clearTimeout(cardsTimer);
      clearTimeout(summaryTimer);
    };
  }, [isActive]);

  return (
    <div className="flex flex-col h-full px-4 py-3">
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="text-[13px] font-semibold text-gray-800">
            {translateInterfaceText("Comparativo de Performance")}</h3>
          <p className="text-[10px] text-gray-500 mt-0.5">
            {translateInterfaceText("Acompanhe qual variação converte mais.")}</p>
          <p className="text-[8px] text-gray-400 mt-0.5">
            {translateInterfaceText("Última atualização: agora")}</p>
        </div>
        <span className="px-2 py-0.5 rounded-full bg-gray-100 text-[8px] text-gray-500 font-medium">
          {translateInterfaceText("Finalizado")}</span>
      </div>

      {/* A/B Cards */}
      <div className="flex gap-2 mb-3">
        <VideoThumb
          conversionRate="1.9%"
          label={translateInterfaceText("Original")}
          imageSrc={thumbOriginal}
          visible={cardsVisible}
          delay={0}
        />
        <VideoThumb
          isWinner
          conversionRate="2.4%"
          label={translateInterfaceText("Variante")}
          imageSrc={thumbVariant}
          visible={cardsVisible}
          delay={100}
        />
      </div>

      {/* Metrics Summary */}
      <MetricsSummary visible={summaryVisible} />

      {/* Flexible spacer for breathing room */}
      <div className="flex-1 min-h-[16px]" />
    </div>
  );
}
